/**
 * Time-formatting helpers for the PWA.
 *
 * Hours are stored as 0-23 ints across the codebase. Some surfaces
 * also accept 24 as an end-of-day sentinel meaning "until midnight"
 * with exclusive-end semantics (`hour < end_hour`). That convention
 * comes from `Charger.guest_time_windows` and the Tariff TOU window
 * schema; both surfaces accept end_hour in 1-24 inclusive.
 *
 * Display is 12-hour AM/PM per founder direction. Schema stays
 * unchanged; only the rendered label converts.
 */

/**
 * Render a 24-hour integer as a 12-hour AM/PM label.
 *
 * Accepts 0-24 inclusive (the wider range covers end_hour's
 * "until midnight" sentinel). Out-of-range or non-finite input
 * returns an empty string rather than throwing — matches the
 * defensive convention `formatCents` and similar helpers use
 * elsewhere in the codebase, where bad input shouldn't be allowed
 * to take down the parent render.
 *
 * Examples:
 *   formatHour12(0)  → "12:00 AM"   (midnight)
 *   formatHour12(1)  → "1:00 AM"
 *   formatHour12(11) → "11:00 AM"
 *   formatHour12(12) → "12:00 PM"   (noon)
 *   formatHour12(13) → "1:00 PM"
 *   formatHour12(23) → "11:00 PM"
 *   formatHour12(24) → "12:00 AM"   (end-of-day sentinel; same label
 *                                    as 0 — calendar context comes
 *                                    from the day picker, not the
 *                                    hour label)
 *   formatHour12(25) → ""           (out of range — defensive)
 *   formatHour12(NaN) → ""
 */
export function formatHour12(hour: number): string {
  if (!Number.isFinite(hour) || hour < 0 || hour > 24) return '';
  // Treat 24 as midnight-of-next-day, same as 0 from the wall-clock
  // perspective. Mod-24 collapses both to the same display label.
  const h = Math.floor(hour) % 24;
  const period = h < 12 ? 'AM' : 'PM';
  // Hour-of-12: midnight (0) and noon (12) both display as "12".
  // Otherwise modulo by 12 for the 1-11 range on either side.
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:00 ${period}`;
}

/**
 * Native short weekday name (e.g. "Mon" / "lun." / "月") for the active
 * locale, keyed by day-of-week integer where Sun = 0 … Sat = 6.
 *
 * Uses Intl so day names come out genuinely native per locale with no
 * translation-file upkeep. Anchored on 2023-01-01 (a Sunday) in UTC and
 * formatted in UTC so the label never shifts by the viewer's timezone.
 * Out-of-range input falls back to an empty string rather than throwing.
 */
export function weekdayShort(dow: number, locale: string): string {
  if (!Number.isInteger(dow) || dow < 0 || dow > 6) return '';
  const anchor = new Date(Date.UTC(2023, 0, 1 + dow));
  return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(anchor);
}
